import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { GraduationCap, ArrowRight, ArrowLeft, Sparkle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";

const STEPS = [
  { key: "headline", label: "What's your current headline?", placeholder: "Software Engineer • Class of 2015", type: "input" },
  { key: "location", label: "Where are you based?", placeholder: "Kochi, India", type: "input" },
  { key: "bio", label: "Tell your story", placeholder: "A short bio about your journey…", type: "textarea" },
  { key: "institution", label: "Your primary institution", placeholder: "Government Model Engineering College", type: "input" },
  { key: "batch", label: "Which batch / class of?", placeholder: "2011 - 2015", type: "input" },
];

export default function Onboarding() {
  const nav = useNavigate();
  const { setUser, user } = useAuth();
  const [step, setStep] = useState(0);
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);
  const pct = Math.round(((step + 1) / (STEPS.length + 1)) * 100);
  const current = STEPS[step];

  const finish = async () => {
    setSaving(true);
    try {
      const payload = {
        headline: data.headline || "",
        location: data.location || "",
        bio: data.bio || "",
      };
      if (data.institution) {
        payload.education = [{ institution: data.institution, batch: data.batch || "", start_year: (data.batch || "").split("-")[0]?.trim() || "", end_year: (data.batch || "").split("-")[1]?.trim() || "" }];
      }
      await api.put("/profiles/me", payload);
      await api.post("/profiles/me/onboard");
      setUser({ ...user });
      toast.success("Your profile is live!");
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const next = () => (step < STEPS.length - 1 ? setStep(step + 1) : finish());

  return (
    <div className="min-h-screen bg-background grain">
      <div className="mx-auto flex min-h-screen max-w-xl flex-col justify-center px-6 py-12">
        <div className="mb-10 flex items-center gap-2">
          <span className="grid h-9 w-9 place-items-center rounded-xl bg-primary text-primary-foreground"><GraduationCap weight="fill" size={20} /></span>
          <span className="font-heading text-lg font-semibold">AlumniPulse™</span>
        </div>
        <div className="mb-8">
          <div className="mb-2 flex items-center justify-between text-xs text-muted-foreground">
            <span className="overline">Step {step + 1} of {STEPS.length}</span>
            <button onClick={finish} className="hover:text-foreground" data-testid="onboarding-skip">Skip for now</button>
          </div>
          <Progress value={pct} className="h-1.5" />
        </div>
        <div key={step} className="animate-fade-up">
          <p className="overline text-secondary mb-3 flex items-center gap-1.5"><Sparkle weight="fill" /> Build your profile</p>
          <h1 className="font-heading text-3xl font-light tracking-tight mb-6">{current.label}</h1>
          {current.type === "textarea" ? (
            <Textarea autoFocus value={data[current.key] || ""} onChange={(e) => setData({ ...data, [current.key]: e.target.value })} placeholder={current.placeholder} rows={4} data-testid="onboarding-input" />
          ) : (
            <Input autoFocus value={data[current.key] || ""} onChange={(e) => setData({ ...data, [current.key]: e.target.value })} placeholder={current.placeholder} onKeyDown={(e) => e.key === "Enter" && next()} data-testid="onboarding-input" />
          )}
        </div>
        <div className="mt-8 flex items-center justify-between">
          <Button variant="ghost" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} className="gap-1.5"><ArrowLeft size={16} /> Back</Button>
          <Button onClick={next} disabled={saving} className="gap-1.5 rounded-full px-6" data-testid="onboarding-next">
            {step === STEPS.length - 1 ? (saving ? "Finishing…" : "Finish") : "Continue"} <ArrowRight size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
}
